import { PureComponent } from 'react';
import { layer, source, feature } from 'react-openlayers';
import { orderBy } from 'lodash';

import EventBus from '../../../EventBus';
import { MISSION_EVENTS } from '../../../missionEvents';
import { MAP_EVENTS } from '../../../mapEvents';
import { getCurrentMission } from '../../../dataModelHelpers';
import { convertProjection4329 } from '../../../utils';
import { HIDE_AT_ZOOM_ZONE } from './zoomThresholds';

const Z_INDEX_PATH_ARROWS = 3; // Above the path line (Z_INDEX_PATH=1).

interface PathArrowsLayerState {
  features: JSX.Element[];
}

/**
 * Overlays directional arrows on the waypoint path at each sample boundary:
 *   - between the last core of one sample and the first of the next.
 *
 * Re-uses PathLayer's event surface. Skips transitions involving routing-only
 * waypoints (those with no related sample).
 */
export default class PathArrowsLayer extends PureComponent<object, PathArrowsLayerState> {
  constructor(props: object) {
    super(props);
    this.state = { features: [] };
    this.rebuildAll = this.rebuildAll.bind(this);
  }

  componentDidMount() {
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.on(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    EventBus.on(MAP_EVENTS.PATH_UPDATED, this.rebuildAll);
    this.rebuildAll();
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.PATH_UPDATED, this.rebuildAll);
  }

  rebuildAll() {
    const mission = getCurrentMission();

    if (!mission) {
      this.setState({ features: [] });

      return;
    }

    const waypoints = orderBy(mission.getWaypoints(), ['waypoint_number'], ['asc']);

    if (waypoints.length < 2) {
      this.setState({ features: [] });

      return;
    }

    const features: JSX.Element[] = [];

    for (let i = 0; i < waypoints.length - 1; i++) {
      const cur = waypoints[i];
      const next = waypoints[i + 1];

      const curSampleId = cur.getRelatedSample()?.sample_id;
      const nextSampleId = next.getRelatedSample()?.sample_id;

      if (!curSampleId || !nextSampleId || curSampleId === nextSampleId) {
        continue;
      }

      const curPx = convertProjection4329([cur.lon, cur.lat]);
      const nextPx = convertProjection4329([next.lon, next.lat]);
      const midpoint: [number, number] = [(curPx[0] + nextPx[0]) / 2, (curPx[1] + nextPx[1]) / 2];

      // The "➤" glyph renders pointing east (right). OpenLayers text
      // rotation is clockwise-positive; screen y grows downward while the
      // projected y-axis grows upward. To align with the projected segment
      // direction, rotate by -atan2(dy, dx).
      const dx = nextPx[0] - curPx[0];
      const dy = nextPx[1] - curPx[1];
      const olRotation = -Math.atan2(dy, dx);

      features.push(
        <feature.PointReact
          key={`path-arrow-${i}`}
          coordinate={midpoint}
          textOptions={{
            text: '➤',
            font: 'bold 20px Calibri,sans-serif',
            fillOptions: { color: '#FFFFFF' },
            strokeOptions: { color: '#000000', width: 3 },
            rotation: olRotation,
          }}
          zIndex={Z_INDEX_PATH_ARROWS}
          hideAtZoom={HIDE_AT_ZOOM_ZONE}
        />,
      );
    }

    this.setState({ features });
  }

  render() {
    return (
      // @ts-ignore - layer.VectorImage children typing
      <layer.VectorImage zIndex={Z_INDEX_PATH_ARROWS}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>{this.state.features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
